import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'
import { tabId, tabPanelId } from './tabIds'

export type TabCountTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export interface TabItem<TKey extends string> {
  key: TKey
  label: ReactNode
  /** Petit compteur après le libellé ; `null` ou absent : rien n'est affiché. */
  count?: number | null
  countTone?: TabCountTone
}

export interface TabsProps<TKey extends string> {
  /** Nom accessible de la liste d'onglets. */
  label: string
  items: readonly TabItem<TKey>[]
  value: TKey
  onChange: (key: TKey) => void
  /** Préfixe des identifiants : relie chaque onglet à son panneau (`tabPanelProps`). */
  idPrefix: string
  className?: string
}

/**
 * Onglets pleins de module (référence visuelle du 11 oct. 2026) : onglet actif plein rouge, inactifs en surface,
 * 38 px. Remplace la `master-search-toolbar` écrite à la main par chaque module. Les flèches, Début et Fin déplacent
 * le focus et activent l'onglet (activation automatique, motif WAI-ARIA « Tabs »).
 */
export function Tabs<TKey extends string>({
  label,
  items,
  value,
  onChange,
  idPrefix,
  className,
}: TabsProps<TKey>) {
  const refs = useRef(new Map<TKey, HTMLButtonElement>())

  const activate = (index: number) => {
    const target = items[(index + items.length) % items.length]
    if (!target) return
    onChange(target.key)
    refs.current.get(target.key)?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') activate(index + 1)
    else if (event.key === 'ArrowLeft') activate(index - 1)
    else if (event.key === 'Home') activate(0)
    else if (event.key === 'End') activate(items.length - 1)
    else return
    event.preventDefault()
  }

  return (
    <div className={mergeClassNames('ds-tabs', className)} role="tablist" aria-label={label}>
      {items.map((item, index) => {
        const selected = item.key === value
        return (
          <button
            key={item.key}
            ref={(element) => {
              if (element) refs.current.set(item.key, element)
              else refs.current.delete(item.key)
            }}
            id={tabId(idPrefix, item.key)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={tabPanelId(idPrefix, item.key)}
            tabIndex={selected ? 0 : -1}
            className={mergeClassNames('ds-tabs__tab', selected && 'ds-tabs__tab--selected')}
            onClick={() => {
              onChange(item.key)
            }}
            onKeyDown={(event) => {
              onKeyDown(event, index)
            }}
          >
            {item.label}
            {item.count === undefined || item.count === null ? null : (
              <span
                className={mergeClassNames(
                  'ds-tabs__count',
                  `ds-tabs__count--${item.countTone ?? 'neutral'}`,
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
